/*
 * Copyright (c) 2025.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * rapportselectie.ts
 * 11-11-2024 - Jelmer Jellema - Spin in het Web B.V. Apeldoorn NL
 * Component RapportselectieComponent
 * Selector: rapport-selectie
 */
import {Component, inject, OnInit} from "@angular/core";
import {WPAIBase} from "./wpaibase";
import {ApiService} from "../services/api.service";
import {discs, profielkeuzes, niveaus} from "./basisdata";
import {FormsModule} from "@angular/forms";
import {hash} from "../types";
import {Studierichting} from "../models/studierichting";
import {sortBy, sortedUniq} from "lodash-es";
import {IDropdownSettings, NgMultiSelectDropDownModule} from "ng-multiselect-dropdown";
import pdfMake from "pdfmake/build/pdfmake";
import {
  Content,
  ContentSvg,
  ContentTable,
  ContentText,
  CustomTableLayout,
  TDocumentDefinitions
} from "pdfmake/interfaces";
import {DateTime} from "luxon";

interface basisdata {
  payment_nodig: boolean,
  bedrag: number,
  assets: string, //assets-url
  fonts: {
    normal: string,
    bold: string
  }
}

@Component({
  standalone: true,
  selector: 'rapport-selectie',
  templateUrl: './rapportselectie.component.html',
  styleUrls: ['studietable.scss', 'rapportselectie.component.scss'],
  imports: [
    FormsModule,
    NgMultiSelectDropDownModule
  ]
})
export class RapportselectieComponent extends WPAIBase implements OnInit {
  basisdata: basisdata = null;

  model = {
    disc: <string>null,
    profielkeuze: <string>null,
    factuurgegevens: <string>"(Anonieme aankoop)"
  };
  factuur: {
    nummer: number,
    datum: string, //iso
    gegevens: string,
    bedrag: number,
  }
  bewaarcode: string = null;
  ophaalbewaarcode: string = ""; //voor ophalen
  bestelstatus: 'aanvraag' | 'betaald' | 'gratis' | 'onbekend' | 'geannuleerd' | 'open' | 'mislukt' | 'fout' = 'aanvraag'; //bezig met aanvraag, zolang we geen betaalresultaat terughebben
  studies: Studierichting[] = [];
  studiesGefilterd: Studierichting[] = [];
  selectedRijNr: number = null;
  filter: {
    niveau?: string,
    sectoren?: string[]
  } = {};
  public dropdownSettings: IDropdownSettings = {
    singleSelection: false,
    /*  idField: 'id',
      textField: 'sector',*/
    selectAllText: 'Alle sectoren',
    unSelectAllText: 'Alle sectoren',
    itemsShowLimit: 10,
    allowSearchFilter: false,
    noDataAvailablePlaceholderText: "(Geen gegevens)"
  };
  /**
   * Even wat dingen goedzetten
   * @protected
   */

  protected niveaus: string[] = [];
  protected sectoren: string[] = [];
  protected readonly discs = discs;
  protected readonly profielkeuzes = profielkeuzes;
  private api = inject(ApiService);

  constructor() {
    super(true);
  }

  public get mijnUrl(): string {
    let url = new URL(window.location.href);
    url.search = "";
    return url.toString();
  }

  /**
   * Leesbare versie van de bewaarcode
 -  */
  public get mijnBewaarcode(): string {
    if (!this.bewaarcode?.length) {
      return "";
    }
    let code = "";
    let i = 0;
    while (this.bewaarcode.length > i) {
      if (code.length) {
        code += '-';
      }
      code += this.bewaarcode.substring(i, i + 4);
      i += 4;
    }
    return code;
  }

  /**
   * Haal de basisdata op
   */
  async ngOnInit() {
    this.log(`Init Rapportselectie`);
    this.basisdata = <basisdata>await this.api.hashFromAction("basisdata");
    this.log(this.basisdata);
    const url = new URL(window.location.href);
    this.bewaarcode = url.searchParams.get("bewaarcode");
    this.log('Bewaarcode:', this.bewaarcode);
    if (this.bewaarcode) {
      await this.stapOphalen();
    }
  }

  /**
   * Maak de bestelling bij het backend. Deze geeft een url voor redirect terug (naar de paymentprovider of direct naar de redirect-url om de resultaten te tonen)
   */
  async bestellen() {
    const data: hash<any> = Object.assign({}, this.model);
    data.url = window.location.href;
    let betaalData = await this.api.hashFromAction("bestel", data);
    this.log(betaalData);
    if (betaalData?.url) {
      //redirecten maar
      window.location = betaalData.url; //na betalen komt er een bewaarcode terug
    }
    //anders is het al mislukt
  }

  /**
   * Haal met de bewaarcode de informatie op, inclusief betaalstatus of, als alles okee is, de studies
   */
  async stapOphalen(poging: number = 0) {
    if (!this.bewaarcode) {
      return; //niks te doen
    }
    let bestelresultaat = await this.api.hashFromAction("ophalenBestelling", {bewaarcode: this.bewaarcode});
    //als dit goed is en met status bestaald, dan zijn de studies er
    this.log(bestelresultaat);
    if (!bestelresultaat) {
      this.bestelstatus = "fout"; //blijkbaar (even?) iets mis
      return;
    }
    this.bestelstatus = bestelresultaat['bestelstatus'];
    if (this.bestelstatus === 'open') {
      //dan doen we het zo nog een keer
      poging++;
      if (poging < 100) {
        setTimeout(() => {
          this.stapOphalen(poging)
        }, poging < 4 ? 10000 : 30000);
      } else {
        this.bestelstatus = 'mislukt';
      }
    } else if (this.bestelstatus === 'betaald' || this.bestelstatus === 'gratis') {
      this.reset();
      this.model = bestelresultaat['bestelling'];
      this.factuur = bestelresultaat['factuur'];
      this.studies = Studierichting.createAll(bestelresultaat['studies'] || []);
      //keuze uit niveaus hangt puur af van de studies die zijn teruggekomen. Bij sectoren hangt het af van het gekozen niveau
      this.niveaus = niveaus.filter(niveau => this.studies.some(studie => studie.niveau === niveau));
      this.log("Beschikbare niveaus", this.niveaus);
      this.doeFilter(); //filter opnieuw
    }
  }

  /**
   * Controleer of een rij in het filter zit
   * @param studie
   */
  inFilter(studie: Studierichting): boolean {
    if (this.filter.niveau?.length && studie.niveau !== this.filter.niveau) {
      return false;
    }
    return !(this.filter.sectoren?.length && (!this.filter.sectoren.includes(studie.sector)));
  }

  /**
   * Run het filter na een wijziging of reset.
   */
  doeFilter(): void {
    //te kiezen sectoren hangen af van het huidige niveau. Dus dat doen we hier opnieuw
    this.sectoren = sortedUniq(
      sortBy(this.studies.filter(studie => this.filter.niveau?.length ? this.filter.niveau.includes(studie.niveau) : true)
        .map(studie => studie.sector)));
    //kijk welke sectoren in het nieuwe filter zitten
    if (this.filter.sectoren?.length) {
      //dit doen we voorzichtig, want opnieuw toekennen van this.filter.sectoren triggert onNgModelChange (en die hebben we nodig om de multiselect te laten werken). Dus alleen als het nodig is
      if (this.filter.sectoren.some(sector => !this.sectoren.includes(sector))) {
        this.filter.sectoren = this.filter.sectoren.filter(sector => this.sectoren.includes(sector));
      }
    }
    this.studiesGefilterd = this.studies.filter(studie => this.inFilter(studie));
    this.selectedRijNr = null;
  }

  /**
   * Maak een pdf van de gefilterde studielijst
   */
  studielijstPDF() {
    /*
    Voor de definitie van het document en de styling, zie https://pdfmake.github.io/docs/0.1/document-definition-object/styling/
     */
    const primaryColor = "#1C2D50";
    const secondaryColor = "#8cab89";
    const secondaryColorDarker = "#829E7F";
    const dataColor = "#E6A96C";
    const dataColorDarker = "#ECAE6F";
    const opmerkingColor = "#FFFFFF";
    const opmerkingColorDarker = "#ECECEC";
    const A4Width = 595.28;
    const A4Height = 841.89;
    const content: Content = []; //gaan we later vullen
    const images = {
      logo: `${this.basisdata.assets}/logo.png`
    };

    const tableLayouts: hash<CustomTableLayout> = {};

    const doc: TDocumentDefinitions = {
      pageSize: 'A4', //pdf punten is 1/72 inch, dus 0,035278 cm. In pdf punten is een A4 [595.28, 841.89]
      pageOrientation: 'portrait',
      pageMargins: [20, 90, 20, 50], //ruimte voor header en footer
      info: {
        title: `Professioneel studiekeuzeadvies Studiebank - ${this.bewaarcode}`,
        creator: 'Professioneelstudiekeuzeadvies.nl'
      },
      defaultStyle: {
        fontSize: 12,
        font: 'ThemeFont'
      },
      images: images,
      content: content,
      //background: this.bewaarcode,
      styles: {
        Kop: {
          fontSize: 16,
          bold: true
        }
      },
      header:
        {
          image: "logo",
          fit: [200, 50],
          marginRight: 20,
          marginTop: 20,
          alignment: 'right'
        },
      footer: {
        fontSize: 8,
        alignment: 'center',
        marginBottom: 10,
        text: `N.B. deze lijst is op geen enkele wijze bindend en geeft slechts een indicatie van passende studierichtingen. Controleer altijd de eisen van de opleiding.\n\n(c) ${DateTime.now().year} Valeri Berns Advies - professioneelstudiekeuzeadvies.nl - ${this.bewaarcode}`
      }
    }

    const defaultMargin = 5;

    //hr over de volle breedte, minus de page marges. Geen marges erboven en eronder
    const hr: ContentSvg = {
      svg: `<svg width="${A4Width - 40}" height="1"><line x1="0" y1="0" x2="${A4Width - 40}" y2="0" stroke="${primaryColor}"></line></svg>`
    };

    const numSectoren = this.filter.sectoren?.length || 0;
    const filterTable = {
      marginTop: defaultMargin * 2,
      layout: 'filterTable',
      table: {
        headerRows: 1,
        widths: [50, 100, 50, 150, 'auto'],
        body: <Content[][]>[
          ['DISC', 'Profiel', 'Niveau', 'Sector', 'Bewaarcode'],
          [this.model.disc,
            this.model.profielkeuze,
            this.filter.niveau?.length ? this.filter.niveau : "(alle)",
            numSectoren ? (numSectoren > 1 ? `${numSectoren} sectoren` : this.filter.sectoren[0]) : "(alle)",
            this.bewaarcode
          ]
        ]
      }
    }

    //even kleuren e.d. zetten
    filterTable.table.body = filterTable.table.body.map(((rij, rijIndex) => {
      return rij.map(cel => {
        return <ContentText>{
          text: cel,
          bold: (rijIndex === 0),
          fillColor: secondaryColor,
          color: '#FFFFFF'
        };
      });
    }));
    //en de bijbehorende layout
    tableLayouts.filterTable = {
      hLineWidth: () => 1,
      hLineColor: () => primaryColor,
      vLineColor: () => primaryColor,
      vLineWidth: (kolom) => (kolom === 0 || kolom === filterTable.table.widths.length) ? 1 : 0,
      paddingTop: () => 8,
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingBottom: () => 8
    }


    //de filtergegevens
    let studieTable: ContentTable = {
      marginTop: defaultMargin * 2,
      layout: 'studieTable',
      table: {
        headerRows: 1,
        widths: [50, 150, 150, '*'],
        body: <Content[][]>[
          ['Niveau', 'Sector', 'Studierichting', 'Voorwaarden'],
        ]
      }
    };
    //vul met de studies
    for (let studie of this.studiesGefilterd) {
      studieTable.table.body.push([
        studie.niveau, studie.sector, this.breakStudierichting(studie,"\n"), studie.profielkeuzeOpmerking(this.model.profielkeuze)
      ]);
    }
    //even kleuren e.d. zetten
    studieTable.table.body = studieTable.table.body.map(((rij, rijIndex) => {
      return rij.map((cel, celIndex) => {
        let color: string;
        let fillColor: string;
        if (rijIndex === 0 || celIndex === 0) {
          color = '#FFFFFF';
          fillColor = (rijIndex % 2 === 0) ? secondaryColor : secondaryColorDarker;
        } else if (rijIndex > 0 && (celIndex === 1 || celIndex === 2)) {
          color = '#000000';
          fillColor = (rijIndex % 2 === 0) ? dataColor : dataColorDarker;
        } else {
          color = "#000000";
          fillColor = (rijIndex % 2 === 0) ? opmerkingColor : opmerkingColorDarker;
        }
        return <ContentText>{
          text: cel,
          fontSize: (rijIndex === 0) ? 10 : 8,
          bold: (rijIndex === 0),
          fillColor: fillColor,
          color: color
        };
      });
    }));
    //en de bijbehorende layout
    tableLayouts.studieTable = {
      hLineWidth: () => 1,
      hLineColor: () => "#FFFFFF",
      vLineColor: () => '#FFFFFF',
      vLineWidth: () => 1,
      paddingTop: () => 4,
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingBottom: () => 4
    }

    //samenstellen content
    content.push(
      {
        text: "Studiebanklijst",
        style: "Kop",
        marginBottom: 2
      },
      hr,
      {
        text: "Welke studie past bij jou?",
        marginTop: defaultMargin + 2
      },
      {
        text: "Hieronder vind je een lijst met studies die passen bij jouw Disc gedragsprofiel, profielkeuze op de middelbare school en interesses.",
        marginTop: defaultMargin
      },
      //tabel met selectie
      filterTable,
      //en de studies
      studieTable
    );

    this.log(pdfMake);
    //maak de pdf, en zet de fonts meteen ook (eerder geeft typescript-gedoe)
    pdfMake.createPdf(
      doc,
      tableLayouts,
      {ThemeFont: this.basisdata.fonts}
    ).open();
  }

  /**
   * Maak een factuur
   */
  async factuurPDF() {
    /*
    Voor de definitie van het document en de styling, zie https://pdfmake.github.io/docs/0.1/document-definition-object/styling/
     */

    if (this.bestelstatus === 'gratis') {
      return; //geen factuur
    }
    const primaryColor = "#1C2D50";
    const secondaryColor = "#8cab89";
    const secondaryColorDarker = "#829E7F";
    const dataColor = "#E6A96C";
    const dataColorDarker = "#ECAE6F";
    const opmerkingColor = "#FFFFFF";
    const opmerkingColorDarker = "#ECECEC";
    const A4Width = 595.28;
    const A4Height = 841.89;
    const content: Content = []; //gaan we later vullen
    const images = {
      logo: `${this.basisdata.assets}/logo.png`
    };

    const tableLayouts: hash<CustomTableLayout> = {};

    const doc: TDocumentDefinitions = {
      pageSize: 'A4', //pdf punten is 1/72 inch, dus 0,035278 cm. In pdf punten is een A4 [595.28, 841.89]
      pageOrientation: 'portrait',
      pageMargins: [20, 90, 20, 30], //ruimte voor header en footer
      info: {
        title: `Professioneel studiekeuzeadvies Studiebank - ${this.bewaarcode} - Factuur ${this.factuur.nummer}`,
        creator: 'Professioneelstudiekeuzeadvies.nl'
      },
      defaultStyle: {
        fontSize: 12,
        font: 'ThemeFont'
      },
      images: images,
      content: content,
      //background: this.bewaarcode,
      styles: {
        Kop: {
          fontSize: 16,
          bold: true
        }
      },
      header:
        {
          image: "logo",
          fit: [200, 50],
          marginRight: 20,
          marginTop: 20,
          alignment: 'right'
        },
      footer: {
        fontSize: 8,
        alignment: 'center',
        marginBottom: 10,
        text: `Valeri Berns Advies - professioneelstudiekeuzeadvies.nl - Van Ruisdaelstraat 45 6814 KW Arnhem - KvK 64841022 -  ${this.bewaarcode}`
      }
    }

    const defaultMargin = 5;

    //hr over de volle breedte, minus de page marges. Geen marges erboven en eronder
    const hr: ContentSvg = {
      svg: `<svg width="${A4Width - 40}" height="1"><line x1="0" y1="0" x2="${A4Width - 40}" y2="0" stroke="${primaryColor}"></line></svg>`
    };

    const numSectoren = this.filter.sectoren?.length || 0;
    const filterTable = {
      marginTop: defaultMargin * 2,
      layout: 'filterTable',
      table: {
        headerRows: 1,
        widths: [50, 100, 50, '*', 'auto'],
        body: <Content[][]>[
          ['DISC', 'Profiel', 'Niveau', 'Sector', 'Bewaarcode'],
          [this.model.disc,
            this.model.profielkeuze,
            this.filter.niveau?.length ? this.filter.niveau : "(alle)",
            numSectoren ? (numSectoren > 1 ? `${numSectoren} sectoren` : this.filter.sectoren[0]) : "(alle)",
            this.bewaarcode
          ]
        ]
      }
    }

    //even kleuren e.d. zetten
    filterTable.table.body = filterTable.table.body.map(((rij, rijIndex) => {
      return rij.map(cel => {
        return <ContentText>{
          text: cel,
          bold: (rijIndex === 0),
          fillColor: secondaryColor,
          color: '#FFFFFF'
        };
      });
    }));
    //en de bijbehorende layout
    tableLayouts.filterTable = {
      hLineWidth: () => 1,
      hLineColor: () => primaryColor,
      vLineColor: () => primaryColor,
      vLineWidth: (kolom) => (kolom === 0 || kolom === filterTable.table.widths.length) ? 1 : 0,
      paddingTop: () => 8,
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingBottom: () => 8
    }

    //het bedrag
    let bruto = Math.round((this.factuur.bedrag / 1.21) * 100) / 100; //afronden op 2 decimalen
    let btw = Math.round(0.21 * bruto * 100) / 100;
    while ((bruto + btw) < this.factuur.bedrag) {
      this.factuur.bedrag += 0.01; //tja
    }
    const euromaker = Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'});

    //samenstellen content
    content.push(
      {
        text: `Factuur PSKA-${this.factuur.nummer} `,
        style: "Kop",
        marginBottom: 2
      },
      hr);

    content.push({
        marginTop: 20,
        marginLeft: 8,
        marginBottom: 20,
        columns: [
          {
            width: '*',
            text: this.factuur.gegevens,
            bold: true,
          },
          {
            alignment: 'right',
            text: `Factuurnummer: PSKA-${this.factuur.nummer}\n\nDatum: ${DateTime.fromSQL(this.factuur.datum).toLocaleString(DateTime.DATE_MED)}`,
            width: 'auto',
          },
        ]
      },
      {
        text: "Voor de opvraag uit onze studiekeuzebank met onderstaand filter, brengen wij je het volgende in rekening:",
        marginBottom: 25
      },
      {
        layout: 'noBorders',
        marginRight: 150,
        marginBottom: 20,
        table: {
          headerRows: 0,
          widths: ['*', 'auto'],
          body: [
            [
              {
                text: "Opvraag studiekeuzebank"
              },
              {
                alignment: 'right',
                text: euromaker.format(bruto)
              }
            ],
            [
              {
                text: "BTW 21%"
              },
              {
                alignment: 'right',
                text: euromaker.format(btw)
              }
            ],
            [
              {
                text: "Betaald via online betaling",
                bold: true,
              },
              {
                alignment: 'right',
                text: euromaker.format(this.factuur.bedrag),
                bold: true
              }
            ]
          ]
        }
      },
      filterTable,
      {
        text: "De factuur is reeds voldaan.",
        marginTop: 25
      }
    );


    this.log(pdfMake);
    //maak de pdf, en zet de fonts meteen ook (eerder geeft typescript-gedoe)
    pdfMake.createPdf(
      doc,
      tableLayouts,
      {ThemeFont: this.basisdata.fonts}
    ).open();
  }

  reset()
    :
    void {
    this.selectedRijNr = null;
    this.filter = {};
    this.doeFilter();
  }

  /**
   * Gebruiker vult een bewaarcode in
   */

  ophalenBewaarcode() {
    const querycode = this.ophaalbewaarcode.replace(/-/g,'');
    if (querycode.length)
    {
      window.location.href = this.mijnUrl + `?bewaarcode=${querycode}`;
    }
  }

  /**
   * Return het studierichtingveld met op de plek van verticale strepen <br>
   * @param studie
   * @param vervanging
   */
  breakStudierichting(studie: Studierichting, vervanging: string = "<br>") {
    return studie.studierichting.replace(/\s*\|\s*/g, vervanging);
  }
}
